import React, { FormEvent, useEffect, useState, useRef } from 'react';
import countryData from "../data/countries.json";
import sellerService from '../services/sellerService';
import { useAppContext } from '../context/AppContext';

interface EditSellerAddressCompProps {
    show: boolean;
    onClose: () => void;
}

interface AddressFormErrors {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
}

const EditSellerAddressComp: React.FC<EditSellerAddressCompProps> = ({ show, onClose }) => {

    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const { seller, setSeller } = useAppContext();

    const [addressLine1, setAddressLine1] = useState(seller!.addressLine1);
    const [addressLine2, setAddressLine2] = useState(seller!.addressLine2);
    const [city, setCity] = useState(seller!.city);
    const [state, setState] = useState(seller!.state);
    const [postcode, setPostCode] = useState(seller!.postcode);
    const [country, setCountry] = useState(seller!.country);

    const [countries] = useState(countryData);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addressFormErrors, setAddressFormErrors] = useState<AddressFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setAddressLine1(seller!.addressLine1);
            setAddressLine2(seller!.addressLine2);
            setCity(seller!.city);
            setState(seller!.state);
            setPostCode(seller!.postcode);
            setCountry(seller!.country);
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateAddress = () => {
        const errors: AddressFormErrors = {};
        if (!addressLine1) {
            errors.addressLine1 = "Address Line 1 is required";
        }
        if (!city) {
            errors.city = "City is required";
        }
        if (!state) {
            errors.state = "State is required";
        }
        const postCodePattern = /^[A-Za-z0-9\s-]{3,10}$/;
        if (!postcode) {
            errors.postcode = "Postcode is required";
        } else if (!postCodePattern.test(postcode)) {
            errors.postcode = "Postcode is invalid";
        }
        if (!country) {
            errors.country = "Country is required";
        }
        setAddressFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleAddressSubmit = (e: FormEvent) => {
        const updateSellerAddress = async () => {
            try {
                const updateSucceed = await sellerService.updateSellerAddress(addressLine1, addressLine2, city, state, postcode, country);
                if (updateSucceed === "Success") {
                    setSeller({ ...seller!, addressLine1: addressLine1, addressLine2: addressLine2, city: city, state: state, postcode: postcode, country: country });
                    onClose();
                } else {
                    setServiceStatus({ type: "Error", message: "Failed to update the address." });
                }
            }
            catch (err) {
                setServiceStatus({ type: "Error", message: "Failed to update the address." });
            }
            setIsSubmitting(false);
        };
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
        if (validateAddress()) {
            setIsSubmitting(true);
            updateSellerAddress();
        }
    };

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Address</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className='row g-3'>
                            <div className="col-md-12">
                                <label htmlFor="inputAddress1" className="form-label">Address Line 1</label>
                                <input
                                    type="text"
                                    className={`form-control ${addressFormErrors.addressLine1 ? ` is-invalid` : ``}`}
                                    id="inputAddress1"
                                    placeholder=""
                                    maxLength={200}
                                    value={addressLine1}
                                    onChange={(e) => setAddressLine1(e.target.value)}
                                />
                                {addressFormErrors.addressLine1 && <div className="invalid-feedback">{addressFormErrors.addressLine1}</div>}
                            </div>
                            <div className="col-md-12">
                                <label htmlFor="inputAddress2" className="form-label">Address Line 2</label>
                                <input
                                    type="text"
                                    className={`form-control ${addressFormErrors.addressLine2 ? ` is-invalid` : ``}`}
                                    id="inputAddress2"
                                    placeholder=""
                                    maxLength={200}
                                    value={addressLine2}
                                    onChange={(e) => setAddressLine2(e.target.value)}
                                />
                                {addressFormErrors.addressLine2 && <div className="invalid-feedback">{addressFormErrors.addressLine2}</div>}
                            </div>
                            <div className="col-md-12">
                                <label htmlFor="inputCity" className="form-label">City</label>
                                <input
                                    type="text"
                                    className={`form-control ${addressFormErrors.city ? ` is-invalid` : ``}`}
                                    id="inputCity"
                                    placeholder=""
                                    maxLength={100}
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                />
                                {addressFormErrors.city && <div className="invalid-feedback">{addressFormErrors.city}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="inputState" className="form-label">State</label>
                                <input
                                    type="text"
                                    className={`form-control ${addressFormErrors.state ? ` is-invalid` : ``}`}
                                    id="inputState"
                                    placeholder=""
                                    maxLength={50}
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                />
                                {addressFormErrors.state && <div className="invalid-feedback">{addressFormErrors.state}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="inputPostcode" className="form-label">Postcode</label>
                                <input
                                    type="text"
                                    className={`form-control ${addressFormErrors.postcode ? ` is-invalid` : ``}`}
                                    id="inputPostcode"
                                    placeholder=""
                                    maxLength={50}
                                    value={postcode}
                                    onChange={(e) => setPostCode(e.target.value)}
                                />
                                {addressFormErrors.postcode && <div className="invalid-feedback">{addressFormErrors.postcode}</div>}
                            </div>
                            <div className="col-md-12">
                                <label htmlFor="inputCountry" className="form-label">Country</label>
                                <select
                                    id="inputCountry"
                                    className={`form-select ${addressFormErrors.country ? ` is-invalid` : ``}`}
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}>
                                    <option disabled value="">Choose...</option>
                                    {countries.map((country, index) => (
                                        <option key={index} value={country.name}>
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                                {addressFormErrors.country && <div className="invalid-feedback">{addressFormErrors.country}</div>}
                            </div>
                            {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                        </div>
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                        <button className="btn btn-outline-dark rounded-0" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-dark rounded-0" onClick={handleAddressSubmit} disabled={isSubmitting}>
                            {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                        </button>
                    </div>
                </div>
            </div >
        </div >
    );

};

export default EditSellerAddressComp;